import React, { useState, FormEvent } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send } from "lucide-react";
import { toast } from "sonner";

interface NewsletterSubscribeProps {
  className?: string;
  buttonText?: string;
  title?: string;
  description?: string;
  showTitle?: boolean;
  showDescription?: boolean;
  centered?: boolean;
}

const NewsletterSubscribe: React.FC<NewsletterSubscribeProps> = ({
  className = '',
  buttonText = 'Subscribe',
  title = '',
  description = '',
  showTitle = true,
  showDescription = true,
  centered = true,
}) => {
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(
        'https://usercom.babbage.systems/submit',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: 'email',
            email
          })
        }
      );
      setSubscriptionStatus('success');
      toast.success(`Thanks for subscribing!`, {
        description: `Your email (${email}) has been successfully added to our newsletter.`,
        duration: 5000,
      });
      setEmail('');
    } catch (error) {
      console.error('Error submitting email:', error);
      setSubscriptionStatus('error');
      toast.error('Subscription failed', {
        description: 'There was an error submitting your email. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubscriptionStatus('idle'), 3000);
    }
  };

  return (
    <div className={`${className} ${centered ? 'text-center' : ''}`}>
      {showTitle && <h3 className="text-lg font-semibold mb-2">{title}</h3>}
      {showDescription && (
        <p className="text-muted-foreground mb-4">
          {description}
        </p>
      )}
      <form onSubmit={handleSignUp} className={`flex gap-2 ${centered ? 'max-w-md mx-auto' : 'w-full'}`}>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="flex-grow"
          aria-label="Email subscription"
          disabled={isSubmitting}
        />
        <Button type="submit" className="shrink-0" disabled={isSubmitting}>
          <Send className="h-4 w-4 mr-2" />
          {buttonText}
        </Button>
      </form>
      {subscriptionStatus === 'success' && (
        <p className="text-green-500 mt-2 text-sm">Thanks for subscribing!</p>
      )}
      {subscriptionStatus === 'error' && (
        <p className="text-red-500 mt-2 text-sm">Something went wrong. Please try again.</p>
      )}
    </div>
  );
};

export default NewsletterSubscribe;
